import React, { ReactNode } from 'react'
import { Box, Grid } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { PairingButton } from 'src/Libs/flow/components'
import { NumberField, TextField } from 'src/Libs/inputs/react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { CommonEvseChargingSettings } from './CommonEvseChargingSettings'
import { CommonEvseDeviceInformation } from './CommonEvseDeviceInformation'

type EvseType = 'eebus' | 'modbusTcp'

interface CommonEvseDeviceSettingsProps {
  evseType: EvseType
  index: number
  pairingId: string
  brand: string
  reference: string
  deviceReference?: string
  deviceId: string
  canEdit: boolean
  canPair: boolean
  handlePairing: () => void
  afterProtection?: ReactNode
  afterMinimumCharge?: ReactNode
}

export const CommonEvseDeviceSettings: React.FC<CommonEvseDeviceSettingsProps> = ({
  evseType,
  index,
  pairingId,
  brand,
  reference,
  deviceReference,
  deviceId,
  canEdit,
  canPair,
  handlePairing,
  afterProtection,
  afterMinimumCharge,
}) => {
  const { t } = useCustomTranslation()
  const fieldPrefix = `chargingStations.${evseType}.${index}`
  const pairingLabel =
    evseType === 'eebus' ? 'productEditor.pairingEditor.pairingID.eebus-wallbox' : 'productEditor.pairingEditor.pairingID.modbus-wallbox'

  return (
    <Grid item xs={12}>
      <SettingsBox
        title={t('installation.evseEditor.deviceName')}
        suffix={<TextField label={t('installation.evseEditor.deviceName')} name={`${fieldPrefix}.name`} disabled={!canEdit} />}
      />
      <CommonEvseDeviceInformation evseType={evseType} brand={brand} reference={reference} deviceReference={deviceReference} />
      <SettingsBox
        title={t(pairingLabel)}
        suffix={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'flex-end', width: ['100%', 'auto'] }}>
            <TextField label={t(pairingLabel)} name={`${fieldPrefix}.pairingId`} disabled />
            <PairingButton size="large" onClick={handlePairing} disabled={!canPair || !canEdit} icon={pairingId ? 'transfer' : 'link'}>
              {pairingId ? t('common.replace') : t('common.pair')}
            </PairingButton>
          </Box>
        }
      />
      <SettingsBox
        title={t('installation.evseEditor.evseProtection')}
        suffix={
          <NumberField
            label={t('installation.evseEditor.evseProtection')}
            suffix={t('affixes.ampere')}
            name={`${fieldPrefix}.protection`}
            min={0}
            max={64}
            disabled={!canEdit}
          />
        }
      />
      {afterProtection}
      <SettingsBox
        title={t('installation.evseEditor.minChargeSubTitle')}
        description={t('installation.evseEditor.minChargeNote')}
        suffix={
          <NumberField
            label={t('installation.evseEditor.minCharge')}
            suffix={t('affixes.ampere')}
            name={`${fieldPrefix}.minCharge`}
            min={6}
            max={32}
            disabled={!canEdit}
          />
        }
      />
      {afterMinimumCharge}

      <CommonEvseChargingSettings evseType={evseType} index={index} deviceId={deviceId} canEdit={canEdit} />
    </Grid>
  )
}
